// pause-animations.component.ts

import { DOCUMENT, NgIf, NgClass } from "@angular/common";
import { Component, OnInit, OnDestroy, Renderer2, inject } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccessibilityService } from "./services/accessibility.service";

@Component({
  selector: "tgsd-pause-animations",
  standalone: true,
  // TEMPLATE: Replaced with the standard template for consistency.
  template: `
    <button
      (click)="toggleState()"
      [ngClass]="{ 'in-use': isActive }"
      [attr.aria-label]="getAriaLabel()"
      [attr.aria-pressed]="isActive"
      aria-live="polite"
      class="feature-item"
    >
      <div class="icon active active-check" *ngIf="isActive">
        <svg
          width="17px"
          height="13px"
          viewBox="0 0 17 13"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <g
            id="Page-1"
            stroke="none"
            stroke-width="1"
            fill="none"
            fill-rule="evenodd"
          >
            <g
              id="Different-States"
              transform="translate(-1004.000000, -279.000000)"
              fill="#ffffff"
            >
              <g
                id="Group-28-Copy-12"
                transform="translate(592.000000, 224.000000)"
              >
                <g id="Group-56" transform="translate(98.000000, 20.000000)">
                  <g
                    id="Group-3-Copy-7"
                    transform="translate(296.000000, 16.000000)"
                  >
                    <path
                      d="M34.5748969,22.1199038 L25.4067007,31.2161926 C24.8673951,31.7554982 24.004506,31.7914519 23.4292466,31.2521463 L18.5754957,26.8298399 C18.0002363,26.2905342 17.9642826,25.3916914 18.4676346,24.8164321 C19.0069402,24.2411727 19.905783,24.205219 20.4810424,24.7445246 L24.3280894,28.2679883 L32.5255354,20.0705423 C33.1007948,19.4952829 33.9996376,19.4952829 34.5748969,20.0705423 C35.1501563,20.6458016 35.1501563,21.5446444 34.5748969,22.1199038 Z"
                      id="Path"
                    ></path>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>

      <span class="feature-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
        </svg>
      </span>

      <span class="feature-label">Pause Animations</span>

      <!-- Note: State indicator dots are not needed for a simple on/off toggle. -->
    </button>
  `,
  imports: [NgIf, NgClass],
  // STYLES: Using the same minimalist style as the contrast component.
  styles: [`
    :host {
      display: block;
    }
    .feature-item {
      position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center;
      width: 100%; min-height: 90px; padding: 12px 8px; text-align: center;
      background-color: transparent; border: none; color: #333; cursor: pointer;
      transition: color 0.2s ease-in-out; font-family: inherit;
    }
    .active-tick svg {
      width: 20px; height: 20px;
    }
    .feature-icon {
      margin-bottom: 8px;
    }
    .feature-icon svg {
      width: 28px; height: 28px;
    }
    .feature-label {
      font-size: 13px; font-weight: 600; line-height: 1.3;
    }
  `]
})
export class PauseAnimationsComponent implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);
  private accessibilityService = inject(AccessibilityService);
  private destroy$ = new Subject<void>();

  public isActive = false;
  private _styleElement: HTMLStyleElement | null = null;

  ngOnInit(): void {
    this.accessibilityService.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // If the feature is active, toggle it off to reset.
        if (this.isActive) {
          this.toggleState();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._cleanupFeature();
  }

  public toggleState(): void {
    this.isActive = !this.isActive;
    this._runStateLogic();
  }

  public getAriaLabel(): string {
    return this.isActive ? "Resume animations" : "Pause animations";
  }

  private _cleanupFeature(): void {
    // Safely remove the style element if it exists.
    this._styleElement?.remove();
    this._styleElement = null;
  }

  private _runStateLogic(): void {
    this._cleanupFeature();

    if (!this.isActive) {
      return;
    }

    // This comprehensive CSS rule pauses animations, transitions, and smooth scrolling.
    // It is scoped to exclude the accessibility widget itself.
    const css = `
      *:not(#ega-accessibility-widget *),
      *::before:not(#ega-accessibility-widget *),
      *::after:not(#ega-accessibility-widget *) {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    `;

    // Create the style element
    this._styleElement = this.renderer.createElement('style');

    // ✅ Null Safety Check: Proceed only if the element was created successfully.
    if (this._styleElement) {
      // ✅ Safe DOM Manipulation: All operations are inside the null guard.
      this._styleElement.textContent = css;
      this.renderer.appendChild(this.document.head, this._styleElement);
    }
  }
}
